import type { Giide, IState, Step } from './graphql/types';
import {
  DEFAULT_SORT_TYPE,
  DEFAULT_VIEW_TYPE,
  getStepToRemoveIndex,
  ITEMS_PER_PAGE,
  reorderSteps,
  sortStepsByOrder,
} from './components/utils';
import { PaymentState } from './types';
import { ModalContent } from './hooks/useGiideContentModal';
import { AiChatHistory } from './graphql/api';

const initialState: IState = {
  getStartedModalOpen: false,
  graphicFormScrollPos: null,
  subscriptions: { giideState: {} },
  steps: [],
  leftSideTabState: 'feed',
  openedStepOrder: 0,
  relinkingFeedcard: null,
  giideListFilter: {
    waiting: false,
    published: false,
    unpublished: false,
    migrating: false,
    transcoding: false,
    error: false,
    review: false,
    pending: false,
    public: false,
    private: false,
  },
  publishOpenedStep: 0,
  giideListSortType: { type: DEFAULT_SORT_TYPE, descending: false },
  giideListPaginationPage: {
    page: 1,
    paginationState: { minValue: 0, maxValue: ITEMS_PER_PAGE },
  },
  giideListViewType: DEFAULT_VIEW_TYPE,
  accountPage: 'Profile',
  upgradeModalOpen: false,
  needToRefetchSubData: false,
  paymentProcessing: false,
  accountDisabled: false,
  paymentState: PaymentState.idle,
  createGiideError: null,
  exportVideoState: {
    id: undefined,
    error: undefined,
    status: undefined,
    downloadLink: undefined,
  },
  imageEditorImage: null,
  imageSubscriptions: new Set(),
  imageChanged: undefined,
  AI: {},
  giideContentModal: ModalContent.none,
  giidePasswordModal: {
    giideId: null,
    modalType: undefined,
  },
  unlockedGiides: [],
  copyGiideModal: null,
};

const giideReducer = (
  state: IState = initialState,
  action: { payload: any; type: string }
): IState => {
  switch (action.type) {
    case 'HIGHLIGHT_CARD':
      return {
        ...state,
        highlightedCardId: action.payload.cardId,
      };
    case 'UPDATE_ONE_GIIDE_SUBSCRIPTION':
      state.subscriptions?.giideState?.[action.payload.id]?.unsubscribe();
      return {
        ...state,
        subscriptions: {
          ...state.subscriptions,
          giideState: {
            ...state.subscriptions?.giideState,
            [action.payload.id]: action.payload.sub,
          },
        },
      };
    case 'UPDATE_GRAPHIC_FORM_SEARCH_SCROLL_POSITION':
      return {
        ...state,
        graphicFormScrollPos: action.payload,
      };
    case 'UPDATE_SUBSCRIPTION':
      state.subscriptions?.[action.payload.key]?.unsubscribe();
      return {
        ...state,
        subscriptions: {
          ...state.subscriptions,
          [action.payload.key]: action.payload.sub,
        },
      };
    case 'SET_GIIDE_FETCHING':
      return { ...state, giideListFetching: action.payload };
    case 'SET_GIIDE_AUDIO_LIBRARY_FETCHING':
      return { ...state, giideAudioLibraryFetching: action.payload };
    case 'CLOSE_RECORDING_ERROR_MODAL':
      return {
        ...state,
        recordingErrorModalVisible: false,
      };
    case 'CLOSE_RECORDING_PROCESSING_MODAL':
      return {
        ...state,
        recordingProcessingModalVisible: false,
        recordingProcessingModalShowed: true,
      };
    case 'CREATE_CHAPTER_FROM_TRANSCRIPTION_MODAL':
      return {
        ...state,
        recordingProcessingModalVisible: false,
        recordingProcessingModalShowed: true,
        newStepModal: true,
      };
    case 'SET_RECORDING_ERROR_MODAL_VISIBLE':
      return { ...state, recordingErrorModalVisible: action.payload };
    case 'SET_RECORDING_PROCESSING_MODAL_VISIBLE':
      return { ...state, recordingProcessingModalVisible: action.payload };
    case 'SET_RECORDING_PROCESSING_MODAL_SHOWED':
      return { ...state, recordingProcessingModalShowed: action.payload };
    case 'SET_NEW_STEP_MODAL_VISIBLE':
      return { ...state, newStepModal: action.payload };
    case 'SET_WAS_PLAYING':
      return {
        ...state,
        player: { ...state.player, wasPlaying: action.payload, playing: false },
      };
    case 'ON_CHANGE':
      state.player?.seekTo?.(Math.round(action.payload) / 1000, 'seconds');
      return {
        ...state,
        player: { ...state.player, progressTime: action.payload },
      };
    case 'ON_BEFORE_CHANGE':
      if (
        state.player?.playing &&
        Date.now() - (state.player?.playingFrom ?? 0) > 200
      ) {
        return {
          ...state,
          player: {
            ...state.player,
            wasPlaying: true,
            playing: false,
          },
        };
      }
      return state;
    case 'ON_AFTER_CHANGE':
      if (state.player?.wasPlaying) {
        return {
          ...state,
          player: {
            ...state.player,
            playing: true,
            wasPlaying: false,
            playingFrom: Date.now(),
          },
        };
      }
      return state;
    case 'SET_PLAYER_DURATION':
      return { ...state, player: { ...state.player, length: action.payload } };
    case 'ADJUST_PLAYER_TIME':
      // workaround for playing start playing again after seeking at the end, even when playing is false.
      let seekTo;
      if (state.player && state.player.progressTime && state.player.length) {
        if (
          state.player.progressTime + action.payload * 1000 <
          state.player.length
        ) {
          seekTo = (state.player?.progressTime ?? 0) / 1000 + action.payload;
        } else {
          // -25 ms to be sure that it doesnt overflow while playing
          // note: -1ms could possibly work too without issue, but 25 is better
          // because there is 20ms interval on player, so its more safe to have some space
          seekTo = (state.player?.length - 25) / 1000;
        }
      }
      // eslint-disable-next-line no-unused-expressions
      state.player?.seekTo?.(seekTo);
      return state;
    case 'SET_PLAYING_STATE':
      return {
        ...state,
        player: {
          ...state.player,
          playing: action.payload,
          wasPlaying: false,
          playingFrom: Date.now(),
        },
      };
    case 'SET_PLAYING_PROGRESS':
      return {
        ...state,
        player: {
          ...state.player,
          progressTime: action.payload,
        },
      };
    case 'SET_SEEK_FUNCTION':
      return { ...state, player: { ...state.player, seekTo: action.payload } };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        userFetching: false,
      };
    case 'UPDATE_USER':
      return { ...state, user: { ...state.user, ...action.payload } };
    case 'SET_GIIDES':
      return { ...state, giides: action.payload, giideListFetching: false };
    case 'SET_LIBRARY_GIIDES':
      return { ...state, libraryGiides: action.payload };
    case 'SET_GIIDE_LIST_FILTER':
      return { ...state, giideListFilter: action.payload };
    case 'SET_GIIDE_LIST_SORT_TYPE':
      return { ...state, giideListSortType: action.payload };
    case 'SET_GIIDE_PAGINATION_PAGE':
      return { ...state, giideListPaginationPage: action.payload };
    case 'SET_GIIDE_LIST_VIEW_TYPE':
      return { ...state, giideListViewType: action.payload };
    case 'SET_GIIDE_LIST_SEARCH':
      return { ...state, giideListSearch: action.payload };
    case 'SET_GIIDE_TAB_STATE':
      if (
        action.payload.compareIndex === undefined ||
        action.payload.compareIndex === state.openedStepOrder
      ) {
        return { ...state, tabState: action.payload.state };
      }
      return state;
    case 'ADD_FEED_CARD':
      return {
        ...state,
        steps: state.steps.map(step =>
          step.id === action.payload.recording.id
            ? // tslint:disable-next-line: no-object-literal-type-assertion
              ({
                ...step,
                recording: {
                  ...step.recording,
                  feedCards: {
                    items: action.payload.feedCardRecordingId
                      ? step!
                          .recording!.feedCards?.items?.concat(action.payload)
                          .sort((a, b) => (a?.time ?? 0) - (b?.time ?? 0))
                      : step!.recording!.feedCards?.items?.map(fc =>
                          fc?.id === action.payload.id ? action.payload : fc
                        ),
                  },
                },
              } as Step)
            : step
        ),
      };
    case 'ADD_GIIDE':
      const index = state.giides?.findIndex(g => g.id === action.payload.id);
      if (index === -1) {
        return { ...state, giides: [...(state.giides || []), action.payload] };
      } else if (index !== undefined) {
        return {
          ...state,
          giides: [
            ...state.giides!.slice(0, index),
            action.payload,
            ...state.giides!.slice(index + 1),
          ],
        };
      }
      return state;
    case 'UPDATE_GIIDE':
      if (action.payload.current) {
        return {
          ...state,
          giide: { ...state.giide, ...action.payload.giide },
        };
      } else if (state.giides) {
        return {
          ...state,
          giides: state.giides!.map(giide =>
            giide.id !== action.payload.giide.id
              ? giide
              : { ...giide, ...action.payload.giide }
          ),
        };
      }
      return state;
    case 'SET_SHOW_BUTTONS':
      return { ...state, showButtons: action.payload };
    case 'UPDATE_GIIDE_LIBRARY':
      action.payload.giide.steps.items.forEach(step => {
        step.isInLibrary = true;
        step.recording.feedCards.items = step.recording.feedCards.items.filter(
          fc =>
            fc?.nonLibraryGiideID
              ? fc.nonLibraryGiideID === state.giide?.id
              : true
        );
        step.recording.feedCards.items.forEach(feedCard => {
          feedCard.isInLibrary = true;
        });
      });
      const steps = [
        state.steps[0],
        ...action.payload.giide.steps.items,
        state.steps[1],
      ];
      steps.forEach((step, index) => {
        step.order = index;
      });
      return {
        ...state,
        steps,
        giide: {
          ...action.payload.giide,
          ...state.giide,
          steps: {
            items: steps,
          },
          giideLibraryTenantID: action.payload.giide.tenantID,
          giideLibraryOwner: action.payload.giide.owner,
          giideLibrarySpeaker: {
            firstName: action.payload.giide.speaker.firstName,
            lastName: action.payload.giide.speaker.lastName,
            fullName: action.payload.giide.speaker.fullName,
            bio: action.payload.giide.speaker.bio,
            profile: action.payload.giide.speaker.profile,
            headshotPath: action.payload.giide.speaker.headshotPath,
          },
          giideLibraryDescription: action.payload.giide.description,
          giideLibraryAlreadyMerged: true,
        },
      };
    case 'DELETE_GIIDE':
      return {
        ...state,
        giides: state.giides!.filter(giide => giide.id !== action.payload),
      };
    case 'SET_GIIDE':
      return { ...state, giide: action.payload };
    case 'SET_STEPS':
      return {
        ...state,
        steps: action.payload.steps,
        openedStepOrder: action.payload.openLast
          ? action.payload.steps[action.payload.steps.length - 1].order
          : state.openedStepOrder,
      };
    case 'REORDER_STEPS':
      const openedStep = state.steps[state.openedStepOrder || 0];
      let tmpSteps = state.steps.sort(sortStepsByOrder);

      const stepToRemoveIndex = getStepToRemoveIndex(
        tmpSteps,
        action.payload.stepId
      );

      tmpSteps = reorderSteps(
        stepToRemoveIndex,
        action.payload.stepNewIndex,
        tmpSteps
      );
      const newOpenedStepOrder = tmpSteps.find(
        step => step.id === openedStep.id
      )?.order;

      return {
        ...state,
        steps: tmpSteps,
        openedStepOrder: newOpenedStepOrder,
      };
    case 'ADD_STEP':
      return {
        ...state,
        steps: [...state.steps, action.payload],
        openedStepOrder: action.payload.order,
        tabState: undefined,
      };
    case 'SET_OPENED_STEP':
      if (action.payload === state.openedStepOrder) return state;
      return {
        ...state,
        openedStepOrder: action.payload,
        player: state.player ? { ...state.player, progressTime: 0 } : undefined,
        relinkingFeedcard: null,
      };
    case 'DELETE_STEP':
      return {
        ...state,
        steps: state.steps.filter(step => step.id !== action.payload),
      };
    case 'REMOVE_STEP':
      return {
        ...state,
        steps: state.steps.filter(step => step.id !== action.payload),
      };
    case 'UPDATE_STEP':
      return {
        ...state,
        steps: state.steps.map(step =>
          step.id !== action.payload.id
            ? { ...step, editing: false }
            : state.giide?.giideLibraryId
            ? { ...step, ...action.payload, order: step.order }
            : { ...step, ...action.payload }
        ),
      };
    case 'SET_PROCESSING_RECORDING':
      return {
        ...state,
        recordingProcessingState: {
          ...state.recordingProcessingState,
          ...action.payload,
        },
      };
    case 'UPDATE_CARD_PROPS':
      return { ...state, cardProps: { ...state.cardProps, ...action.payload } };
    case 'UPDATE_EDITING_CARD_PROPS':
      return {
        ...state,
        cardProps: { ...state.cardProps, ...action.payload },
        editingCardProps: { ...state.cardProps, ...action.payload },
      };
    case 'ADD_CARD_PROPS':
      const cardProps = state.cardProps || {};
      cardProps[action.payload.key] = action.payload.value;
      return { ...state, cardProps };
    case 'SET_CARD_PROPS':
      return { ...state, cardProps: action.payload };
    case 'SET_EDITING_CARD_PROPS':
      return {
        ...state,
        cardProps: action.payload,
        editingCardProps: action.payload,
      };
    case 'LOGOUT':
      return initialState;
    case 'SET_ADMIN_USERS':
      return { ...state, adminUsers: action.payload };
    case 'SET_AVATAR':
      return { ...state, avatar: action.payload };
    case 'SET_LIBRARY_AVATAR':
      return { ...state, libraryAuthorAvatar: action.payload };
    case 'ADD_ADMIN_USER':
      return {
        ...state,
        adminUsers: [...(state.adminUsers || []), action.payload],
      };
    case 'SET_TENANT_CATEGORIES':
      return { ...state, tenantCategories: action.payload };
    case 'SET_IS_SCRAPING':
      return { ...state, isScraping: action.payload };
    case 'SET_IS_INIT_SCRAPING_DONE':
      return { ...state, isInitialScrapingDone: action.payload };
    case 'MOVE_FEED_CARD':
      return {
        ...state,
        steps: state.steps.map(step => {
          if (step.id === action.payload.recordingId) {
            return {
              ...step,
              recording: {
                ...step.recording,
                feedCards: {
                  items: step
                    .recording!.feedCards?.items?.map(feedCard => {
                      if (feedCard?.id === action.payload.cardId) {
                        return {
                          ...feedCard,
                          time: action.payload.startTime,
                        };
                      }
                      return feedCard;
                    })
                    .sort((a, b) => (a?.time ?? 0) - (b?.time ?? 0)),
                },
              },
            } as Step;
          }
          return step;
        }),
      };
    case 'UNLINK_RECORDING':
      return {
        ...state,
        relinkingFeedcard: null,
        steps: state.steps.map(step => {
          if (step.id === action.payload.id) {
            return {
              ...step,
              recording: {
                ...step.recording,
                deleted: true,
                filePath: '',
                length: 0,
                transcriptPath: null,
                transcriptResults: null,
                updatedAt: action.payload.timestamp,
                feedCards: {
                  items: step.recording!.feedCards?.items?.map(feedCard => {
                    return {
                      ...feedCard,
                      unlinked: true,
                    };
                  }),
                },
              },
            } as Step;
          }
          return step;
        }),
      };
    case 'RELINK_FEEDCARD':
      return {
        ...state,
        relinkingFeedcard: null,
        steps: state.steps.map(step => {
          if (step.id === action.payload.recordingId) {
            return {
              ...step,
              recording: {
                ...step.recording,
                feedCards: {
                  items: step.recording!.feedCards?.items?.map(feedCard => {
                    if (feedCard?.id === action.payload.feedcardId) {
                      return {
                        ...feedCard,
                        unlinked: false,
                        time: action.payload.time,
                      };
                    }
                    return feedCard;
                  }),
                },
              },
            } as Step;
          }
          return step;
        }),
      };
    case 'UNLINK_FEEDCARD':
      return {
        ...state,
        relinkingFeedcard: null,
        steps: state.steps.map(step => {
          if (step.id === action.payload.recordingId) {
            return {
              ...step,
              recording: {
                ...step.recording,
                feedCards: {
                  items: step.recording!.feedCards?.items?.map(feedCard => {
                    if (feedCard?.id === action.payload.feedcardId) {
                      return {
                        ...feedCard,
                        unlinked: true,
                        time: action.payload.time,
                      };
                    }
                    return feedCard;
                  }),
                },
              },
            } as Step;
          }
          return step;
        }),
      };
    case 'SET_RELINKING_FEEDCARD':
      return {
        ...state,
        relinkingFeedcard: action.payload,
      };
    case 'UPDATE_GIIDE_SETTINGS':
      return {
        ...state,
        giide: { ...state.giide, ...action.payload },
        giides: state.giides?.map(giide =>
          giide.id !== action.payload.id
            ? giide
            : { ...giide, cardDetails: action.payload.cardDetails }
        ),
      };
    case 'SET_AUDIO_LIBRARY':
      return {
        ...state,
        audioLibrary: action.payload,
      };
    case 'ADD_TO_AUDIO_LIBRARY':
      // to be sure that the subscription for create do not add duplicate items in case we have dispatched action in code
      if (
        state.audioLibrary &&
        state.audioLibrary.length > 0 &&
        state.audioLibrary.find(item => item && item.id === action.payload.id)
      ) {
        return state;
      }
      return {
        ...state,
        audioLibrary: [action.payload, ...(state.audioLibrary || [])],
      };
    case 'UPDATE_AUDIO_LIBRARY':
      return {
        ...state,
        audioLibrary: state.audioLibrary?.map(item => {
          if (item && item.id === action.payload.id) {
            return {
              ...item,
              ...action.payload,
            };
          }
          return item;
        }),
      };
    case 'DELETE_FROM_AUDIO_LIBRARY':
      return {
        ...state,
        audioLibrary: state.audioLibrary!.filter(
          item => item?.id !== action.payload
        ),
      };
    case 'SET_FEEDCARD_VALIDATION_FAILED':
      return { ...state, feedcardValidationFailed: action.payload };
    case 'SELECT_AUDIO_FROM_LIBRARY':
      return {
        ...state,
        recordingProcessingState: {
          ...state.recordingProcessingState,
          state: action.payload.processingState,
          stepId: action.payload.id,
        },
        tabState: 'transcript',
        steps: state.steps.map(step => {
          if (step.id === action.payload.id) {
            return {
              ...step,
              ownRecordingUploaded: true,
              recording: {
                ...step.recording,
                length: action.payload.length,
                transcriptPath: null,
                transcriptResults: null,
                transcriptError: null,
                filePath: action.payload.filePath,
                deleted: false,
              },
            } as Step;
          }
          return step;
        }),
      };
    case 'SET_PUBLISH_ACTIVE_STEP':
      return { ...state, publishOpenedStep: action.payload };
    case 'SET_ACCOUNT_PAGE':
      return { ...state, accountPage: action.payload };
    case 'SET_EDITING_CHAPTER_ID':
      return { ...state, editingChapterId: action.payload };
    case 'SET_AUDIO_UPLOAD_PROGRESS':
      return { ...state, audioUploadProgress: action.payload };
    case 'SET_AUDIO_UPLOAD_FILE_NAME':
      return { ...state, audioUploadFileName: action.payload };
    case 'SET_AUDIO_UPLOAD_FILE_ID':
      return { ...state, audioUploadFileId: action.payload };
    case 'SET_LEFT_SIDE_TAB_STATE':
      return { ...state, leftSideTabState: action.payload };
    case 'UPDATE_SCRIPT':
      return {
        ...state,
        steps: state.steps.map(step =>
          step.id !== action.payload.id
            ? { ...step, editing: false }
            : state.giide?.giideLibraryId
            ? { ...step, ...action.payload, order: step.order }
            : { ...step, ...action.payload }
        ),
      };
    case 'SET_USER_FETCHING':
      return { ...state, userFetching: action.payload };
    case 'SET_USER_LOGGING_IN':
      return { ...state, userLoggingIn: action.payload };
    case 'SET_PREVIEW_DETAILS':
      return { ...state, previewDetails: action.payload };
    case 'SET_USE_TEMPLATE':
      return { ...state, useTemplate: action.payload };
    case 'SET_OLD_GIIDE_DATA':
      return { ...state, oldGiideData: action.payload };
    case 'SET_NEW_IMG_LOADED_BLOB':
      return { ...state, newImgLoadedBlob: action.payload };
    case 'SET_IMAGE_EDITOR_STATE':
      return { ...state, imageEditorState: action.payload };
    case 'SET_PUBLISH_FLOW_GIIDE_BEFORE_PREVIEW':
      return { ...state, publishFlowGiideBeforePreview: action.payload };
    case 'CLEAR_EDITOR_DATA':
      let dataToClear: {} = {
        imageEditorState: undefined,
        newImgLoadedBlob: undefined,
        useTemplate: undefined,
        imageEditorValidation: undefined,
        imageEditorImage: null,
      };
      if (action.payload) {
        dataToClear = {
          ...dataToClear,
          oldGiideData: undefined,
          publishFlowGiideBeforePreview: undefined,
          previewDetails: undefined,
          imageEditorImage: null,
        };
      }
      return { ...state, ...dataToClear };
    case 'SET_IMAGE_EDITOR_VALIDATION_FAILED':
      return { ...state, imageEditorValidation: action.payload };
    case 'SET_ACTIVE_WORD_IN_TRANSCRIBE':
      return { ...state, activeWordInTranscribe: action.payload };
    case 'SET_ADD_FEEDCARD_MODAL_OPENED':
      return { ...state, addFeedcardModalOpened: action.payload };
    case 'SET_IMAGE_MIME_TYPE_LOADED':
      return { ...state, imageMimeTypeLoaded: action.payload };
    case 'UPDATE_GIIDE_SCRIPT':
      return {
        ...state,
        giide: {
          ...state.giide,
          script: action.payload.script,
        } as Giide,
      };
    case 'SET_NEW_FEEDCARD_SAVING':
      return { ...state, newFeedcardSaving: action.payload };
    case 'SET_OWN_RECORDING_STARTED':
      return { ...state, ownRecordingStarted: action.payload };
    case 'SET_GET_STARTED_MODAL_OPEN':
      return { ...state, getStartedModalOpen: action.payload };
    case 'SET_UPGRADE_MODAL_OPEN':
      return { ...state, upgradeModalOpen: action.payload };
    case 'SET_NEED_TO_REFETCH_SUB_DATA':
      return { ...state, needToRefetchSubData: action.payload };
    case 'SET_ANALYTICS_ID':
      return { ...state, analyticsID: action.payload };
    case 'SET_ANALYTICS_DATA':
      return { ...state, analyticsData: action.payload };
    case 'SET_PADDLE_ACCOUNT_DETAILS':
      return { ...state, paddleAccountDetails: action.payload };
    case 'SET_PADDLE_TRANSACTIONS':
      return { ...state, paddleTransactions: action.payload };
    case 'SET_PAYMENT_PROCESSING':
      return { ...state, paymentProcessing: action.payload };
    case 'SET_TRANSCRIPT_DATA':
      return { ...state, transcriptData: action.payload };
    case 'SET_ACCOUNT_DISABLED':
      return { ...state, accountDisabled: action.payload };
    case 'SET_PAYMENT_STATE':
      return { ...state, paymentState: action.payload };
    case 'SET_CREATE_GIIDE_ERROR':
      return { ...state, createGiideError: action.payload };
    case 'SET_EXPORT_VIDEO_STATE':
      return {
        ...state,
        exportVideoState: { ...state.exportVideoState, ...action.payload },
      };
    case 'SET_IMAGE_EDITOR_IMAGE':
      return {
        ...state,
        imageEditorImage: { ...state.imageEditorImage, ...action.payload },
      };
    case 'CLEAR_IMAGE_EDITOR_IMAGE':
      return {
        ...state,
        imageEditorImage: null,
      };
    case 'ADD_IMAGE_SUBSCRIPTION_ID':
      const beforeAddState = new Set(state.imageSubscriptions);
      beforeAddState.add(action.payload);

      return {
        ...state,
        imageSubscriptions: beforeAddState,
      };
    case 'REMOVE_IMAGE_SUBSCRIPTION_ID':
      const beforeRemoveState = new Set(state.imageSubscriptions);
      beforeRemoveState.delete(action.payload);
      return {
        ...state,
        imageSubscriptions: beforeRemoveState,
      };
    case 'SET_IMAGE_CHANGED':
      return {
        ...state,
        imageChanged: action.payload,
      };
    case 'UPDATE_CONVERSATION_HISTORY':
      let newState = { ...state.AI };

      const entry: AiChatHistory = {
        type: action.payload.type,
        content: action.payload.content,
        __typename: 'AiChatHistory',
      };

      if (!newState[action.payload.giide]) {
        newState[action.payload.giide] = { conversationHistory: [entry] };
      } else {
        newState[action.payload.giide].conversationHistory = [
          ...newState[action.payload.giide].conversationHistory,
          entry,
        ];
      }

      return {
        ...state,
        AI: newState,
      };
    case 'SET_AI_CONVERSATION_HISTORY':
      let newAiState = { ...state.AI };

      newAiState[action.payload.giideId] = {
        conversationHistory: action.payload.chatHistory,
      };

      return {
        ...state,
        AI: newAiState,
      };
    case 'SET_GIIDE_CONTENT_MODAL':
      return {
        ...state,
        giideContentModal: action.payload,
      };
    case 'SET_GIIDE_PASSWORD_MODAL':
      return {
        ...state,
        giidePasswordModal: {
          giideId: action.payload.giideId,
          modalType: action.payload.modalType,
        },
      };
    case 'UNLOCK_GIIDE':
      return {
        ...state,
        unlockedGiides: [...state.unlockedGiides, action.payload],
      };
    case 'LOCK_GIIDE':
      const prevUnlockedGiides = [...state.unlockedGiides];
      const newUnlockedGiides = prevUnlockedGiides.filter(
        id => id !== action.payload
      );
      return {
        ...state,
        unlockedGiides: newUnlockedGiides,
      };
    case 'SET_COPY_GIIDE_MODAL':
      return {
        ...state,
        copyGiideModal: action.payload,
      };
    default:
      return state;
  }
};

export default giideReducer;
